// @flow
import InputBase from './InputBase';
import type { InputContextType, CommonInputProps } from './InputBase';

export type AtomicInputProps<ValueType> = CommonInputProps<ValueType>;
type AtomicInputState<DomValueType> = {
  domValue: DomValueType,
};
/**
 * An atomic input is self-sufficient in that it needs no child inputs to be
 * able to edit its value. This is as opposed to `CompoundInput`s which rely
 * on their child inputs to actually do anything - e.g. `Form` and
 * `RepeatingInput`.
 *
 * Most inputs will extend `AtomicInput`.
 *
 * @url https://i.imgur.com/NDlKr83.gif
 */
export default class AtomicInput<
  ValueType = any,
  DomValueType = any,
  InputProps: AtomicInputProps<ValueType> = any,
> extends InputBase<ValueType, InputProps, AtomicInputState<DomValueType>> {
  constructor(props: InputProps, context: InputContextType) {
    super(props, context);
    let domValue: DomValueType;
    if (this.parent) {
      domValue = this.valueToDomValue(this.parent.getCurrentValueElement(this.name));
    } else {
      domValue = this.valueToDomValue(this.receivedValue);
    }

    this.state = { domValue };
  }

  get domValue(): DomValueType {
    return this.state.domValue;
  }

  get currentValue(): ValueType {
    return super.currentValue;
  }

  set currentValue(value: ValueType) {
    super.currentValue = value;
    this.setState({
      domValue: this.valueToDomValue(super.currentValue),
    });
  }

  // eslint-disable-next-line no-unused-vars
  domValueToValue(domValue: DomValueType): ValueType {
    throw new Error('not implemented');
  }

  // eslint-disable-next-line no-unused-vars
  valueToDomValue(value: ValueType): DomValueType {
    throw new Error('not implemented');
  }

  inputDidChange(domValue: DomValueType) {
    if (this.domValue !== domValue) {
      this.setState({ domValue });
    }
    const newValue = this.cleanValue(this.domValueToValue(domValue));
    if (!this.areValuesEqual(newValue, this.currentValue)) {
      this.notifyInputDidChange(newValue);
      super.currentValue = newValue;
    }
  }

  inputDidCommitChange() {
    if (this.areValuesEqual(this.currentValue, this.committedValue)) {
      return;
    }
    this.notifyInputDidCommitChange(this.currentValue);
    this.committedValue = this.currentValue;
    this.validate();
  }
}
